import { useDebounceValue } from 'noddi-util';
import { ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  flattenMultiSelectOptions,
  isMultiSelectGroupedOptions,
  type MultiSelectGroup,
  type MultiSelectOption
} from '../../../base/multi-select-utils';
import { NoddiTooltip } from '../../Overlay/NoddiTooltip';
import { MultiSelect } from './MultiSelect';

type Value = string | number;

export interface NoddiAsyncMultiSelectProps {
  options: MultiSelectOption[] | MultiSelectGroup[] | undefined;
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  defaultOptions?: MultiSelectOption[] | undefined; // preselected items (subset of options or external)
  onValueChange: (values: Value[]) => void;
  placeholder?: string;
  className?: string;
  label?: ReactNode;
  tooltip?: string;
  maxCount?: number;
  // Async support
  isLoading: boolean;
  onInputChange: (value: string) => void;
  // Disabled state
  disabled?: boolean;
  // Search debounce delay
  debounceDelay?: number;
  // Control clearing search when popover closes
  clearSearchOnClose?: boolean;
  hideSelectAll?: boolean;
  renderOption?: (option: MultiSelectOption) => ReactNode;
}

export const NoddiAsyncMultiSelect = ({
  options = [],
  defaultOptions = [],
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  onValueChange,
  placeholder,
  className,
  label,
  tooltip,
  maxCount = 3,
  isLoading = false,
  onInputChange,
  disabled = false,
  debounceDelay = 500,
  clearSearchOnClose = false,
  hideSelectAll,
  renderOption
}: NoddiAsyncMultiSelectProps) => {
  const [selectedValues, setSelectedValues] = useState(defaultOptions.map((o) => o.value));
  // form.reset changes these ids after mount. A new array with the same ids must not replace the selection.
  const defaultValuesKey = defaultOptions.map((option) => String(option.value)).join('\0');
  const appliedDefaultsKeyRef = useRef(defaultValuesKey);

  const generatedId = useId();
  const controlId = id ?? name ?? generatedId;
  const labelIdentifier = `${controlId}-label`;

  // Raw search input coming from underlying MultiSelect
  const [searchInput, setSearchInput] = useState('');
  // Debounced search input
  const debouncedSearch = useDebounceValue({ value: searchInput, delay: debounceDelay });

  // Handle debounced search
  useEffect(() => {
    if (onInputChange) {
      onInputChange(debouncedSearch);
    }
  }, [debouncedSearch, onInputChange]);

  useEffect(() => {
    if (appliedDefaultsKeyRef.current === defaultValuesKey) {
      return;
    }
    appliedDefaultsKeyRef.current = defaultValuesKey;
    setSelectedValues(defaultOptions.map((option) => option.value));
  }, [defaultOptions, defaultValuesKey]);

  // Handle value changes
  const handleValueChange = (values: Value[]) => {
    setSelectedValues(values);
    onValueChange(values);
  };

  // Merge defaultOptions with options to ensure selected items can be displayed
  // even when options haven't loaded yet
  const mergedOptions = useMemo((): MultiSelectOption[] | MultiSelectGroup[] => {
    if (!defaultOptions?.length) {
      return options;
    }

    if (isMultiSelectGroupedOptions(options)) {
      const existingValues = new Set(options.flatMap((group) => group.options.map((o) => String(o.value))));
      const missingDefaults = defaultOptions.filter((o) => !existingValues.has(String(o.value)));
      if (missingDefaults.length === 0) {
        return options;
      }
      return [...options, { heading: 'Selected', options: missingDefaults }];
    }

    const map = new Map<string, MultiSelectOption>();
    defaultOptions.forEach((o) => {
      map.set(String(o.value), o);
    });
    options.forEach((o) => {
      map.set(String(o.value), o);
    });
    return Array.from(map.values());
  }, [options, defaultOptions]);

  const flatMergedOptions = useMemo(() => flattenMultiSelectOptions(mergedOptions), [mergedOptions]);

  return (
    <div className='flex flex-col gap-0.5'>
      {label && (
        <div className='flex items-center gap-2'>
          <label id={labelIdentifier} htmlFor={controlId} className='text-sm'>
            {label}
          </label>
          {tooltip && (
            <NoddiTooltip
              content={tooltip}
              label={typeof label === 'string' ? `More information about ${label}` : 'More information'}
            />
          )}
        </div>
      )}
      <MultiSelect
        options={mergedOptions}
        defaultValue={selectedValues.filter((v) => flatMergedOptions.some((o) => o.value === v))}
        onValueChange={handleValueChange}
        id={controlId}
        name={name}
        aria-labelledby={label ? labelIdentifier : undefined}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        placeholder={placeholder}
        className={className}
        maxCount={maxCount}
        disabled={disabled}
        onSearchValueChange={setSearchInput}
        clearSearchOnClose={clearSearchOnClose}
        hideSelectAll={hideSelectAll}
        loading={isLoading}
        renderOption={renderOption}
      />
    </div>
  );
};
